import { useIsAllowed } from '@/app/navigation/access';
import { Panel, RoutedPanelProps } from '@/components/Layout/Panel/Panel';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsLink } from '@/components/ui/tabs';
import { useUserPreference } from '@/features/user-preferences/user-preferences.hooks';
import { useCurrentUser } from '@/features/users/users.hooks';
import { ReactNode } from 'react';
import { HolidaySettings } from '../holidays/components/HolidaySettings';
import { EmailChangeForm } from './components/EmailChangeForm';
import { PasswordForm } from './components/PasswordForm';
import { ProfileForm } from './components/ProfileForm';
import { TagAuthStatus } from './components/TagAuthStatus';
import { ThemeSelector } from './components/ThemeSelector';
import { TicketNotificationsSetting } from './components/TicketNotificationsSetting';

function TimelineOrderPreference() {
  const [reverseOrder, setReverseOrder] = useUserPreference(
    'reverseTimelineOrder',
  );
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="grid gap-1">
        <Label htmlFor="reverse-timeline-order">Reverse timeline order</Label>
        <span className="text-muted-foreground text-xs">
          Show timeline events chronologically (oldest first) with the message
          composer at the bottom.
        </span>
      </div>
      <Switch
        id="reverse-timeline-order"
        checked={reverseOrder}
        onCheckedChange={setReverseOrder}
      />
    </div>
  );
}

function Section({
  title,
  description,
  children,
  className,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={className ?? 'space-y-4'}>
      <div>
        <h3 className="text-lg font-medium">{title}</h3>
        {description && (
          <p className="text-muted-foreground text-sm">{description}</p>
        )}
      </div>
      {children}
    </section>
  );
}

export const SettingsPanel = ({
  params,
  path,
  basePath,
  ...panelProps
}: RoutedPanelProps) => {
  const { tab: tabParam } = params;
  const user = useCurrentUser();
  const { internal: isInternal } = useIsAllowed();
  // External accounts only get the personal tab (the route closes the other
  // tabs, see routes.ts); the tab bar is then noise.
  const tab: string = isInternal ? tabParam || 'personal' : 'personal';

  return (
    <Panel {...panelProps}>
      <div className="p-8">
        <Tabs value={tab} className="w-full">
          {isInternal && (
            <TabsList className="mb-6 w-full">
              <TabsLink to="/settings/personal" value="personal">
                Personal
              </TabsLink>
              <TabsLink to="/settings/general" value="general">
                General
              </TabsLink>
              <TabsLink to="/settings/auth" value="auth">
                Authentication
              </TabsLink>
              <TabsLink to="/settings/notifications" value="notifications">
                Notifications
              </TabsLink>
            </TabsList>
          )}
          <TabsContent value="personal">
            <div className="space-y-8">
              <Section title="Personal info">
                {user ? (
                  <ProfileForm user={user} />
                ) : (
                  <p className="text-muted-foreground text-sm">
                    Sign in to edit your personal info.
                  </p>
                )}
              </Section>
              {user && (
                <Section title="Email address">
                  <EmailChangeForm user={user} />
                </Section>
              )}
              {/* Timeline order concerns tickets, which externals never see. */}
              {isInternal && (
                <Section
                  title="Preferences"
                  description="Stored on this device only."
                  className="space-y-4 border-t pt-6"
                >
                  <TimelineOrderPreference />
                </Section>
              )}
              <Section
                title="Appearance"
                description="Choose how the interface looks. Auto follows your system."
                className="space-y-3 border-t pt-6"
              >
                <ThemeSelector />
              </Section>
            </div>
          </TabsContent>

          <TabsContent value="general">
            <div className="space-y-8">
              <Section
                title="Holiday calendars"
                description="Manage holiday calendars that appear in date pickers throughout the application."
              >
                <HolidaySettings />
              </Section>
            </div>
          </TabsContent>

          <TabsContent value="auth">
            <div className="space-y-8">
              <Section
                title="Username/password authentication"
                description="Choose a new password for signing in with your username."
              >
                <PasswordForm />
              </Section>
              {isInternal && user && (
                <Section
                  title="Tag authentication"
                  className="space-y-4 border-t pt-6"
                >
                  <TagAuthStatus user={user} />
                </Section>
              )}
            </div>
          </TabsContent>

          <TabsContent value="notifications">
            {isInternal ? (
              <Section title="Notifications">
                <TicketNotificationsSetting />
              </Section>
            ) : (
              <p className="text-muted-foreground text-sm">
                There are no notification settings for your account.
              </p>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Panel>
  );
};
